/**
 * The painted slide, nothing else.
 *
 * Shared by the in-window overlay and the second-display window so a deck
 * cannot look one way on the laptop and another on the projector. Chrome —
 * next/prev, blank, fullscreen — stays with the caller.
 */

import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { drawSlide } from "@/lib/slide-render";
import type { Slide } from "@/lib/slides";
import type { SlideStyle } from "@/lib/slide-themes";
import { cn } from "@/lib/utils";

const MAX_PIXEL_RATIO = 2;

interface PresenterStageProps {
  slides: Slide[];
  index: number;
  blanked: boolean;
  style: SlideStyle;
  className?: string;
}

export function PresenterStage({
  slides,
  index,
  blanked,
  style,
  className,
}: PresenterStageProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  const total = slides.length;
  const slide = slides[Math.min(index, Math.max(0, total - 1))];

  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    const stage = stageRef.current;
    if (!canvas || !stage) return;

    const { width: stageWidth, height: stageHeight } = stage.getBoundingClientRect();
    if (stageWidth < 1 || stageHeight < 1) return;

    const aspect = style.aspect.width / style.aspect.height;
    const boxWidth = Math.min(stageWidth, stageHeight * aspect);
    const boxHeight = boxWidth / aspect;

    canvas.style.width = `${boxWidth}px`;
    canvas.style.height = `${boxHeight}px`;

    if (!slide) return;

    const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
    canvas.width = Math.round(boxWidth * ratio);
    canvas.height = Math.round(boxHeight * ratio);

    const context = canvas.getContext("2d");
    if (!context) return;

    const scale = (boxWidth * ratio) / style.aspect.width;
    context.setTransform(scale, 0, 0, scale, 0, 0);
    drawSlide(context, slide, { total, ...style });
  }, [slide, total, style]);

  useLayoutEffect(() => {
    paint();
  }, [paint]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const observer = new ResizeObserver(() => paint());
    observer.observe(stage);
    window.addEventListener("resize", paint);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", paint);
    };
  }, [paint]);

  return (
    <div
      ref={stageRef}
      className={cn(
        "absolute inset-0 flex items-center justify-center overflow-hidden bg-black",
        className,
      )}
    >
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className={cn("block transition-opacity duration-200", blanked && "opacity-0")}
      />
      {slides.length === 0 && (
        <p className="absolute inset-0 flex items-center justify-center text-sm text-white/70">
          This document has no slides yet.
        </p>
      )}
    </div>
  );
}
